import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { BodyText, Div, Gutter, Headline, InlineNotification, Loader, Page, Title } from '@gaiads/telia-react-component-library';
import _ from 'lodash';

import {
  InvoicingAccountOptionResponseContents,
  UserPoliciesResponseSingleDevicePolicyEnum,
  UserRole,
} from '@cdaas/api-clients/dist/asset-user-openapi';
import {
  CheckoutDetails as CheckoutDetailsReusable,
  CheckoutDetailsTranslations,
  COUNTRIES,
  DefaultValueData,
  FormData,
} from '@cdaas/reusable-components-daas';
import { InvoicingAccountSelectionTranslations } from '@cdaas/reusable-components-daas/dist/src/components/InvoicingAccountSelection/types';

import { shopSetDeliveryDetails } from '../../../../store/actions';
import { RootState } from '../../../../store/reducers';
import { Delivery, User } from '../../../../types';
import { getInvoicingAccounts, hasUserARequiredRole, parsingHelpers, setStorageItems, typeGuards } from '../../../../utils';

import CheckoutProductCart from '../CheckoutProductCart/CheckoutProductCart';

import classes from './CheckoutDetails.module.css';

export interface CheckoutDetailsProps {
  isMobile?: boolean;
}

const CheckoutDetails: FunctionComponent<CheckoutDetailsProps> = ({ isMobile }) => {
  const history = useHistory();

  const { t: tShop } = useTranslation('shop');
  const { t: tGlobal } = useTranslation('globalComponents');

  const user = useSelector((state: RootState) => state.authentication.user, _.isEqual);
  const deliveryDetailsStore = useSelector((state: RootState) => state.shop.delivery, _.isEqual);

  const [deliveryDetails, setDeliveryDetails] = useState<Delivery>();
  const [defaultDeliveryValues, setDefaultDeliveryValues] = useState<DefaultValueData>();
  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [inheritedInvoicingAccount, setInheritedInvoicingAccount] = useState<string>('');

  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const dispatch = useDispatch();

  const userHasAdminRole = hasUserARequiredRole([UserRole.Admin]);
  const userHasOrdererRole = hasUserARequiredRole([UserRole.Orderer]);
  const showLocationReference = userHasAdminRole || userHasOrdererRole;
  const showInvoicingAccount = userHasAdminRole || userHasOrdererRole;

  const initDeliveryDetailsFields = (userOrDeliveryDetails: User | Delivery) => {
    if (typeGuards.isUser(userOrDeliveryDetails)) {
      const parsedNumber = userOrDeliveryDetails.phone_number
        ? parsingHelpers.extractPartsFromFullPhoneNumber(userOrDeliveryDetails.phone_number)
        : undefined;

      setDeliveryDetails({
        deliveryContact: {
          first_name: userOrDeliveryDetails.given_name ?? '',
          last_name: userOrDeliveryDetails.family_name ?? '',
          email: userOrDeliveryDetails.email ?? '',
          phone_number: parsedNumber?.nationalNumber.toString() ?? '',
          country_calling_code: parsedNumber?.countryCallingCode.toString() ?? '',
          title: '',
        },
        deliveryAddress: {
          name: '',
          address1: userOrDeliveryDetails.user_group?.default_delivery_address?.address1 ?? '',
          address2: userOrDeliveryDetails.user_group?.default_delivery_address?.address2 ?? '',
          street: userOrDeliveryDetails.user_group?.default_delivery_address?.street ?? '',
          city: userOrDeliveryDetails.user_group?.default_delivery_address?.city ?? '',
          zip: userOrDeliveryDetails.user_group?.default_delivery_address?.zip ?? '',
          country: userOrDeliveryDetails.user_group?.default_delivery_address?.country ?? '',
        },
        isHomeDelivery: false,
        locationReference: '',
        invoicingAccountNumber: '',
      });
    } else {
      setDeliveryDetails(userOrDeliveryDetails);
    }
  };

  useEffect(() => {
    if (user && (deliveryDetailsStore?.deliveryAddress || deliveryDetailsStore?.deliveryContact)) {
      initDeliveryDetailsFields(deliveryDetailsStore);
    }
  }, [user, deliveryDetailsStore]);

  const getContactInfoFromUser = (userToExtract: User | null | undefined) => {
    if (!userToExtract) {
      return {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        country_calling_code: '',
      };
    }

    const parsedNumber = userToExtract.phone_number ? parsingHelpers.extractPartsFromFullPhoneNumber(userToExtract.phone_number) : undefined;

    return {
      first_name: userToExtract.given_name || '',
      last_name: userToExtract.family_name || '',
      email: userToExtract.email || '',
      phone_number: parsedNumber?.nationalNumber.toString() || '',
      country_calling_code: parsedNumber?.countryCallingCode.toString() || '',
    };
  };

  const getAddressInfoFromUser = (userToExtract: User | null | undefined) => {
    const defaultAddress = userToExtract?.user_group?.default_delivery_address;
    return {
      street: defaultAddress?.street || '',
      address1: defaultAddress?.address1 || '',
      address2: defaultAddress?.address2 || '',
      zip: defaultAddress?.zip || '',
      city: defaultAddress?.city || '',
    };
  };

  useEffect(() => {
    const values: DefaultValueData = {
      ...getContactInfoFromUser(undefined),
      ...getAddressInfoFromUser(undefined),
      // Set organization country for now because there's only one supported country
      country: user?.organization?.locale.country || '',
      location_reference: '',
      invoicing_account_number: '',
    };

    if (orderingOnBehalfUserData.status) {
      // Order on-behalf of user: use user's default delivery address
      Object.assign(values, getContactInfoFromUser(orderingOnBehalfUserData.userToOrderFor));
      Object.assign(values, getAddressInfoFromUser(orderingOnBehalfUserData.userToOrderFor));
    } else if (orderingForOrganization) {
      // Order on-behalf of organization: leave address fields empty
      Object.assign(values, getContactInfoFromUser(user));
    } else {
      // Regular order for self: use current user's information and default delivery address
      Object.assign(values, getContactInfoFromUser(user));
      Object.assign(values, getAddressInfoFromUser(user));
    }

    setDefaultDeliveryValues(values);

    setInheritedInvoicingAccount(
      orderingForOrganization
        ? selectedOrganization?.call_off_number || ownOrganization?.call_off_number || ''
        : orderingOnBehalfUserData.userToOrderFor
          ? orderingOnBehalfUserData.userToOrderFor.invoicing_account?.name ||
            orderingOnBehalfUserData.userToOrderFor.inherited_invoicing_account.name
          : user?.invoicing_account?.name || user?.inherited_invoicing_account.name || '',
    );
  }, [user, orderingForOrganization, orderingOnBehalfUserData, selectedOrganization?.call_off_number, ownOrganization?.call_off_number]);

  useEffect(() => {
    async function fetchData() {
      const invoicingAccounts = await getInvoicingAccounts();
      setOrganizationInvoicingAccounts(invoicingAccounts || []);
      setLoading(false);
    }

    if (showInvoicingAccount) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [showInvoicingAccount]);

  const handleSavingDeliveryDetailsData = (data: FormData): void => {
    const address = {
      name: '',
      address1: data.address1,
      address2: data.address2,
      street: data.street,
      city: data.city,
      zip: data.zip,
      country: data.country,
    };
    const contact = {
      title: '',
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number,
      country_calling_code: data.country_calling_code,
      email: data.email,
    };
    const locationReference = data.location_reference.trim() === '' ? null : data.location_reference;
    const invoicingAccount = data.invoicing_account_number.trim() === '' ? null : data.invoicing_account_number;
    dispatch(shopSetDeliveryDetails(address, contact, data.homeDelivery, locationReference, invoicingAccount));
    setStorageItems(['tDelivery'], [{ contact, address, isHomeDelivery: data.homeDelivery }], 'session');
  };

  const onSubmit = (data: FormData): void => {
    handleSavingDeliveryDetailsData(data);
    history.push('/shop/checkout/delivery');
  };

  const handleGoBackClick = (): void => {
    history.goBack();
  };

  const translation: CheckoutDetailsTranslations = {
    companyName: tShop('checkout.detailsPage.companyName'),
    employeeTitle: tShop('checkout.detailsPage.employeeTitle'),
    billingTitle: tShop('checkout.detailsPage.billingTitle'),
    deliveryTitle: tShop('checkout.detailsPage.deliveryTitle'),
    billingTextOne: tShop('checkout.detailsPage.billingTextOne'),
    billingTextTwo: tShop('checkout.detailsPage.billingTextTwo'),
    deliveryText: tShop('checkout.detailsPage.deliveryText'),
    deliveryQuestion: tShop('checkout.detailsPage.deliveryQuestion'),
    firstName: tShop('checkout.detailsPage.form.firstName'),
    lastName: tShop('checkout.detailsPage.form.lastName'),
    email: tShop('checkout.detailsPage.form.email'),
    phoneNbr: tShop('checkout.detailsPage.form.phoneNbr'),
    address1: tShop('checkout.detailsPage.form.address1'),
    address2: tShop('checkout.detailsPage.form.address2'),
    streetAddress: tShop('checkout.detailsPage.form.streetAddress'),
    postalCode: tShop('checkout.detailsPage.form.postalCode'),
    city: tShop('checkout.detailsPage.form.city'),
    country: tShop('checkout.detailsPage.form.country'),
    countryPlaceholder: tShop('checkout.detailsPage.form.countryPlaceholder'),
    companyDeliveryOption: tShop('checkout.detailsPage.form.companyDeliveryOption'),
    companyDeliveryOptionDetails: tShop('checkout.detailsPage.form.companyDeliveryOptionDetails'),
    homeDeliveryOption: tShop('checkout.detailsPage.form.homeDeliveryOption'),
    homeDeliveryOptionDetails: tShop('checkout.detailsPage.form.homeDeliveryOptionDetails'),
    location: tShop('checkout.detailsPage.form.location'),
    locationReference: tShop('checkout.detailsPage.form.locationReference'),
    locationReferenceHint: tShop('checkout.detailsPage.form.locationReferenceHint'),
    invoicingAccountTitle: tShop('checkout.detailsPage.form.invoicingAccount.title'),
    required: tShop('checkout.detailsPage.form.rules.required'),
    invalidEmail: tShop('checkout.detailsPage.form.rules.invalidEmail'),
    invalidPhoneNumber: tShop('checkout.detailsPage.form.rules.invalidPhoneNumber'),
    invalidPostalCode: tShop('checkout.detailsPage.form.rules.invalidPostalCode'),
    nextButton: tShop('checkout.detailsPage.nextButton'),
    cancelButton: tShop('checkout.detailsPage.cancelButton'),
    Finland: tGlobal('countries.Finland'),
    Sweden: tGlobal('countries.Sweden'),
    Norway: tGlobal('countries.Norway'),
    Denmark: tGlobal('countries.Denmark'),
    UnitedKingdom: tGlobal('countries.UnitedKingdom'),
  };

  const invoicingAccountTranslations: InvoicingAccountSelectionTranslations = {
    dropdownLabel: tShop('checkout.detailsPage.form.invoicingAccount.dropdownLabel'),
    clearSelection: tShop('checkout.detailsPage.form.invoicingAccount.clearSelection'),
    helperText: tShop('checkout.detailsPage.form.invoicingAccount.helperText'),
    noOptionsFoundInfo: tShop('checkout.detailsPage.form.invoicingAccount.noOptionsFoundInfo'),
  };

  const renderOrderingForOrganization = () => (
    <>
      <Title size="xs" margin={{ top: 'lg', bottom: 'xs' }} color={'black'}>
        {tShop('checkout.detailsPage.organizationTitle')}
      </Title>
      <Div className={classes['employee-or-organization-title']} margin={{ top: 'lg', bottom: 'xlg' }} color={'gray800'}>
        {selectedOrganization?.name || ownOrganization?.name}
      </Div>
    </>
  );

  const renderOrderingOnBehalfUser = () => (
    <>
      <Title size="xs" margin={{ top: 'lg', bottom: 'xs' }} color={'black'}>
        {tShop('checkout.detailsPage.employeeTitle')}
      </Title>
      <Div className={classes['employee-or-organization-title']} margin={{ top: 'lg', bottom: 'xs' }} color={'gray800'}>
        {`${orderingOnBehalfUserData.userToOrderFor?.given_name ?? ''} ${orderingOnBehalfUserData.userToOrderFor?.family_name ?? ''}`}
      </Div>
      <BodyText size="sm" color="gray500">
        {`${orderingOnBehalfUserData.userToOrderFor?.email}, ${orderingOnBehalfUserData.userToOrderFor?.phone_number}`}
      </BodyText>
    </>
  );

  if (loading) {
    return (
      <Page>
        <Page.Row alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <Loader />
        </Page.Row>
      </Page>
    );
  }

  const isOnBehalfOrder = orderingForOrganization || orderingOnBehalfUserData.status;

  const isOrderingDisabled = () => {
    if (orderingForOrganization) {
      return false;
    }
    if (orderingOnBehalfUserData.status) {
      return orderingOnBehalfUserData.userToOrderFor?.policies?.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect;
    }
    return user?.policies?.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect;
  };

  return (
    <>
      {isOrderingDisabled() && (
        <>
          <InlineNotification variant="error" title={tShop('singleDevicePolicyLimitFull.title')}>
            <Div>
              <BodyText color="gray500" size="sm">
                {tShop(orderingOnBehalfUserData.status ? 'singleDevicePolicyLimitFull.textOnBehalf' : 'singleDevicePolicyLimitFull.text')}
              </BodyText>
            </Div>
          </InlineNotification>
          <Gutter size="xxlg" />
        </>
      )}
      <Headline size={'md'}>{tShop('checkout.detailsPage.headline')}</Headline>
      <Div shown={isOnBehalfOrder}>
        {orderingForOrganization && renderOrderingForOrganization()}
        {orderingOnBehalfUserData.status && renderOrderingOnBehalfUser()}
      </Div>
      <CheckoutDetailsReusable
        companyName={selectedOrganization?.name || ownOrganization?.name || ''}
        handleBackClick={handleGoBackClick}
        handleNextClick={onSubmit}
        handleSavingDataOnBlurSubmitRestore={handleSavingDeliveryDetailsData}
        // Support only organization's country for now
        countries={COUNTRIES.filter(ccc => ccc.value.toLowerCase() === user?.organization?.locale.country.toLowerCase())}
        countryCodes={COUNTRIES}
        deliveryDetails={deliveryDetails}
        defaultValues={defaultDeliveryValues}
        prefillUserGroupDefaultIfDeliveryDetailsNotAvailable={true}
        showLocationReference={showLocationReference}
        showInvoicingAccount={showInvoicingAccount}
        inheritedInvoicingAccount={inheritedInvoicingAccount}
        invoicingAccounts={organizationInvoicingAccounts}
        translations={translation}
        invoicingAccountTranslations={invoicingAccountTranslations}
        ProductCart={isMobile ? <CheckoutProductCart isMobile /> : undefined}
        nextButtonDisabled={isOrderingDisabled()}
      />
    </>
  );
};

export default CheckoutDetails;
